import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import { supabase } from "../supabaseClient";
import QRCode from "qrcode";
import CropperModal from "../components/CropperModal";
import LogoCropperModal from "../components/LogoCropperModal";
import CardBackgroundCropperModal from "../components/CardBackgroundCropperModal";
import DigitalCard from "../components/DigitalCard";
import { startStripePortal } from "../utils/stripe";
import { trackGoal } from "../utils/datafast";
import { sortLicensesByState } from "../utils/licenses";
import { DEFAULT_CARD_COLOR } from "../constants/cardDefaults";

const API_BASE = process.env.REACT_APP_API_URL || "";

const convertImageToWebp = (file, { maxWidth = 1400, maxHeight = 900, quality = 0.82 } = {}) =>
  new Promise((resolve) => {
    if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const targetWidth = Math.round(img.width * ratio);
      const targetHeight = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            resolve(file);
            return;
          }
          const name = `${file.name.replace(/\.[^/.]+$/, "") || "image"}.webp`;
          resolve(new File([blob], name, { type: "image/webp" }));
        },
        "image/webp",
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });

const formatPhoneDisplay = (value) => {
  const digits = (value || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return value || "";
};

const normalizeSocialUrl = (value, baseUrl) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  const withoutAt = trimmed.replace(/^@/, "");
  if (withoutAt.includes(".") || withoutAt.includes("/")) {
    return `https://${withoutAt}`;
  }
  if (baseUrl) return `${baseUrl}${withoutAt}`;
  return `https://${withoutAt}`;
};

const normalizeWebsiteUrl = (value) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed}`;
};

const formatWebsiteLabel = (url, fallback = "Website") => {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    return host || fallback;
  } catch {
    return fallback;
  }
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const slugify = (value) =>
  (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

const extractSlugFromLink = (link) => {
  if (!link) return "";
  try {
    const url = new URL(link);
    const parts = url.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || "";
  } catch {
    return "";
  }
};

const SOCIAL_ICON_SVGS = {
  Twitter:
    '<svg width="16" height="16" viewBox="0 0 44 40" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block;"><path d="M34.6526 0H41.3995L26.6594 16.847L44 39.7719H30.4225L19.7881 25.8681L7.61989 39.7719H0.868864L16.6349 21.7522L0 0H13.9222L23.5348 12.7087L34.6526 0ZM32.2846 35.7336H36.0232L11.8908 3.82626H7.87892L32.2846 35.7336Z" fill="currentColor"/></svg>',
  Facebook:
    '<svg width="16" height="16" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block;"><g clip-path="url(#clip0_fb)"><path d="M48 24C48 10.7453 37.2547 0 24 0C10.7453 0 0 10.7453 0 24C0 35.255 7.74912 44.6995 18.2026 47.2934V31.3344H13.2538V24H18.2026V20.8397C18.2026 12.671 21.8995 8.8848 29.9194 8.8848C31.44 8.8848 34.0637 9.18336 35.137 9.48096V16.129C34.5706 16.0694 33.5866 16.0397 32.3645 16.0397C28.4294 16.0397 26.9088 17.5306 26.9088 21.4061V24H34.7482L33.4013 31.3344H26.9088V47.8243C38.7926 46.3891 48.001 36.2707 48.001 24H48Z" fill="#0866FF"/><path d="M33.4003 31.3344L34.7472 24H26.9078V21.4061C26.9078 17.5306 28.4285 16.0397 32.3635 16.0397C33.5856 16.0397 34.5696 16.0694 35.136 16.129V9.48096C34.0627 9.1824 31.439 8.8848 29.9184 8.8848C21.8986 8.8848 18.2016 12.671 18.2016 20.8397V24H13.2528V31.3344H18.2016V47.2934C20.0582 47.7542 22.0003 48 23.999 48C24.983 48 25.9536 47.9395 26.9069 47.8243V31.3344H33.3994H33.4003Z" fill="white"/></g><defs><clipPath id="clip0_fb"><rect width="48" height="48" fill="white"/></clipPath></defs></svg>',
  Instagram:
    '<svg width="16" height="16" viewBox="0 0 2500 2500" xmlns="http://www.w3.org/2000/svg" style="display:block;"><defs><radialGradient id="instaOuterSig" cx="332.14" cy="2511.81" r="3263.54" gradientUnits="userSpaceOnUse"><stop offset="0.09" stop-color="#fa8f21"/><stop offset="0.78" stop-color="#d82d7e"/></radialGradient><radialGradient id="instaInnerSig" cx="1516.14" cy="2623.81" r="2572.12" gradientUnits="userSpaceOnUse"><stop offset="0.64" stop-color="#8c3aaa" stop-opacity="0"/><stop offset="1" stop-color="#8c3aaa"/></radialGradient></defs><path d="M833.4,1250c0-230.11,186.49-416.7,416.6-416.7s416.7,186.59,416.7,416.7-186.59,416.7-416.7,416.7S833.4,1480.11,833.4,1250m-225.26,0c0,354.5,287.36,641.86,641.86,641.86S1891.86,1604.5,1891.86,1250,1604.5,608.14,1250,608.14,608.14,895.5,608.14,1250M1767.27,582.69a150,150,0,1,0,150.06-149.94h-0.06a150.07,150.07,0,0,0-150,149.94M745,2267.47c-121.87-5.55-188.11-25.85-232.13-43-58.36-22.72-100-49.78-143.78-93.5s-70.88-85.32-93.5-143.68c-17.16-44-37.46-110.26-43-232.13-6.06-131.76-7.27-171.34-7.27-505.15s1.31-373.28,7.27-505.15c5.55-121.87,26-188,43-232.13,22.72-58.36,49.78-100,93.5-143.78s85.32-70.88,143.78-93.5c44-17.16,110.26-37.46,232.13-43,131.76-6.06,171.34-7.27,505-7.27s373.28,1.31,505.15,7.27c121.87,5.55,188,26,232.13,43,58.36,22.62,100,49.78,143.78,93.5s70.78,85.42,93.5,143.78c17.16,44,37.46,110.26,43,232.13,6.06,131.87,7.27,171.34,7.27,505.15s-1.21,373.28-7.27,505.15c-5.55,121.87-25.95,188.11-43,232.13-22.72,58.36-49.78,100-93.5,143.68s-85.42,70.78-143.78,93.5c-44,17.16-110.26,37.46-232.13,43-131.76,6.06-171.34,7.27-505.15,7.27s-373.28-1.21-505-7.27M734.65,7.57c-133.07,6.06-224,27.16-303.41,58.06C349,97.54,279.38,140.35,209.81,209.81S97.54,349,65.63,431.24c-30.9,79.46-52,170.34-58.06,303.41C1.41,867.93,0,910.54,0,1250s1.41,382.07,7.57,515.35c6.06,133.08,27.16,223.95,58.06,303.41,31.91,82.19,74.62,152,144.18,221.43S349,2402.37,431.24,2434.37c79.56,30.9,170.34,52,303.41,58.06C868,2498.49,910.54,2500,1250,2500s382.07-1.41,515.35-7.57c133.08-6.06,223.95-27.16,303.41-58.06,82.19-32,151.86-74.72,221.43-144.18s112.18-139.24,144.18-221.43c30.9-79.46,52.1-170.34,58.06-303.41,6.06-133.38,7.47-175.89,7.47-515.35s-1.41-382.07-7.47-515.35c-6.06-133.08-27.16-224-58.06-303.41-32-82.19-74.72-151.86-144.18-221.43S2150.95,97.54,2068.86,65.63c-79.56-30.9-170.44-52.1-303.41-58.06C1632.17,1.51,1589.56,0,1250.1,0S868,1.41,734.65,7.57" fill="url(#instaOuterSig)"/><path d="M833.4,1250c0-230.11,186.49-416.7,416.6-416.7s416.7,186.59,416.7,416.7-186.59,416.7-416.7,416.7S833.4,1480.11,833.4,1250m-225.26,0c0,354.5,287.36,641.86,641.86,641.86S1891.86,1604.5,1891.86,1250,1604.5,608.14,1250,608.14,608.14,895.5,608.14,1250M1767.27,582.69a150,150,0,1,0,150.06-149.94h-0.06a150.07,150.07,0,0,0-150,149.94M745,2267.47c-121.87-5.55-188.11-25.85-232.13-43-58.36-22.72-100-49.78-143.78-93.5s-70.88-85.32-93.5-143.68c-17.16-44-37.46-110.26-43-232.13-6.06-131.76-7.27-171.34-7.27-505.15s1.31-373.28,7.27-505.15c5.55-121.87,26-188,43-232.13,22.72-58.36,49.78-100,93.5-143.78s85.32-70.88,143.78-93.5c44-17.16,110.26-37.46,232.13-43,131.76-6.06,171.34-7.27,505-7.27s373.28,1.31,505.15,7.27c121.87,5.55,188,26,232.13,43,58.36,22.62,100,49.78,143.78,93.5s70.78,85.42,93.5,143.78c17.16,44,37.46,110.26,43,232.13,6.06,131.87,7.27,171.34,7.27,505.15s-1.21,373.28-7.27,505.15c-5.55,121.87-25.95,188.11-43,232.13-22.72,58.36-49.78,100-93.5,143.68s-85.42,70.78-143.78,93.5c-44,17.16-110.26-37.46-232.13-43-131.76-6.06-171.34-7.27-505.15-7.27s-373.28-1.21-505-7.27M734.65,7.57c-133.07,6.06-224,27.16-303.41,58.06C349,97.54,279.38,140.35,209.81,209.81S97.54,349,65.63,431.24c-30.9,79.46-52,170.34-58.06,303.41C1.41,867.93,0,910.54,0,1250s1.41,382.07,7.57,515.35c6.06,133.08,27.16,223.95,58.06,303.41,31.91,82.19,74.62,152,144.18,221.43S349,2402.37,431.24,2434.37c79.56,30.9,170.34,52,303.41,58.06C868,2498.49,910.54,2500,1250,2500s382.07-1.41,515.35-7.57c133.08-6.06,223.95-27.16,303.41-58.06,82.19-32,151.86-74.72,221.43-144.18s112.18-139.24,144.18-221.43c30.9-79.46,52.1-170.34,58.06-303.41,6.06-133.38,7.47-175.89,7.47-515.35s-1.41-382.07-7.47-515.35c-6.06-133.08-27.16-224-58.06-303.41-32-82.19-74.72-151.86-144.18-221.43S2150.95,97.54,2068.86,65.63c-79.56-30.9-170.44-52.1-303.41-58.06C1632.17,1.51,1589.56,0,1250.1,0S868,1.41,734.65,7.57" fill="url(#instaInnerSig)"/></svg>',
  LinkedIn:
    '<svg width="16" height="16" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block;"><path d="M44.4567 0H3.54333C2.60358 0 1.70232 0.373315 1.03782 1.03782C0.373315 1.70232 0 2.60358 0 3.54333V44.4567C0 45.3964 0.373315 46.2977 1.03782 46.9622C1.70232 47.6267 2.60358 48 3.54333 48H44.4567C45.3964 48 46.2977 47.6267 46.9622 46.9622C47.6267 46.2977 48 45.3964 48 44.4567V3.54333C48 2.60358 47.6267 1.70232 46.9622 1.03782C46.2977 0.373315 45.3964 0 44.4567 0ZM14.3067 40.89H7.09V17.9667H14.3067V40.89ZM10.6933 14.79C9.87473 14.7854 9.07583 14.5384 8.39747 14.0802C7.71911 13.622 7.19168 12.9731 6.88175 12.2154C6.57183 11.4577 6.4933 10.6252 6.65606 9.82291C6.81883 9.02063 7.2156 8.28455 7.79631 7.70756C8.37702 7.13057 9.11563 6.73853 9.91893 6.58092C10.7222 6.42331 11.5542 6.50719 12.3099 6.82197C13.0656 7.13675 13.7111 7.66833 14.1649 8.34962C14.6188 9.03092 14.8606 9.83138 14.86 10.65C14.8677 11.1981 14.765 11.7421 14.558 12.2496C14.351 12.7571 14.044 13.2178 13.6551 13.6041C13.2663 13.9905 12.8037 14.2946 12.2948 14.4983C11.786 14.702 11.2413 14.8012 10.6933 14.79ZM40.9067 40.91H33.6933V28.3867C33.6933 24.6933 32.1233 23.5533 30.0967 23.5533C27.9567 23.5533 25.8567 25.1667 25.8567 28.48V40.91H18.64V17.9833H25.58V21.16H25.6733C26.37 19.75 28.81 17.34 32.5333 17.34C36.56 17.34 40.91 19.73 40.91 26.73L40.9067 40.91Z" fill="#0A66C2"/></svg>',
};

const svgToPngDataUrl = (svgText, size = 20, iconSize = size) =>
  new Promise((resolve) => {
    if (!svgText) {
      resolve("");
      return;
    }
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve("");
        return;
      }
      const clampedIconSize = Math.min(size, Math.max(1, iconSize));
      const offset = (size - clampedIconSize) / 2;
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, offset, offset, clampedIconSize, clampedIconSize);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve("");
    img.src = svgUrl;
  });

export default function MyInfoPage() {
  const [tab, setTab] = useState("personal");
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    links: [],
    businessStreet: "",
    businessCity: "",
    businessState: "",
    businessZip: "",
    businessCounty: "",
    twitter: "",
    facebook: "",
    instagram: "",
    linkedin: "",
    profilePhoto: null,
    profilePhotoPath: "",
    signatureLogoUrl: "",
    signatureLogoPath: "",
    signatureColor: DEFAULT_CARD_COLOR,
    publicLink: "",
    publicSlug: "",
    visibility: {
      email: true,
      phone: true,
      company: true,
      title: true,
      businessAddress: true,
      twitter: true,
      facebook: true,
      instagram: true,
      linkedin: true,
    },
    subscriptionStatus: "Inactive",
    subscriptionTier: "free",
    cardColor: DEFAULT_CARD_COLOR,
    cardBackgroundUrl: "",
    cardBackgroundPath: "",
  });
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [error, setError] = useState("");
  const [cropSrc, setCropSrc] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [licenseStats, setLicenseStats] = useState({
    active: 0,
    pending: 0,
    expiring: 0,
    issues: 0,
  });
  const [licenses, setLicenses] = useState([]);
  const [visibleLicenseIds, setVisibleLicenseIds] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [originalProfile, setOriginalProfile] = useState(null);
  const [cardColor, setCardColor] = useState(DEFAULT_CARD_COLOR);
  const [cardColorInput, setCardColorInput] = useState(DEFAULT_CARD_COLOR);
  const [cardColorDirty, setCardColorDirty] = useState(false);
  const [cardColorSaving, setCardColorSaving] = useState(false);
  const [signatureColor, setSignatureColor] = useState(DEFAULT_CARD_COLOR);
  const [signatureColorInput, setSignatureColorInput] = useState(DEFAULT_CARD_COLOR);
  const [signatureColorDirty, setSignatureColorDirty] = useState(false);
  const [signatureColorSaving, setSignatureColorSaving] = useState(false);
  const [cardBackgroundUploading, setCardBackgroundUploading] = useState(false);
  const [cardBackgroundRemoving, setCardBackgroundRemoving] = useState(false);
  const [showCardBackgroundCropper, setShowCardBackgroundCropper] = useState(false);
  const [cardBackgroundCropSrc, setCardBackgroundCropSrc] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [vanitySlugInput, setVanitySlugInput] = useState("");
  const [vanitySlugDirty, setVanitySlugDirty] = useState(false);
  const [vanitySlugSaving, setVanitySlugSaving] = useState(false);
  const [signatureCopied, setSignatureCopied] = useState(false);
  const [signatureTextCopied, setSignatureTextCopied] = useState(false);
  const [signatureLogoUploading, setSignatureLogoUploading] = useState(false);
  const [signatureLogoRemoving, setSignatureLogoRemoving] = useState(false);
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [logoCropSrc, setLogoCropSrc] = useState("");
  const [signatureLogoRatio, setSignatureLogoRatio] = useState(null);
  const [signatureSocialPngs, setSignatureSocialPngs] = useState({});
  const [signatureVisibility, setSignatureVisibility] = useState({
    photo: true,
    logo: true,
    name: true,
    title: true,
    company: true,
    email: true,
    phone: true,
    publicLink: true,
    address: false,
    links: false,
    socials: false,
  });
  const fileRef = useRef(null);
  const cardBackgroundRef = useRef(null);
  const signatureLogoRef = useRef(null);
  const userIdRef = useRef(null);
  const copyTimerRef = useRef(null);
  const signatureTimerRef = useRef(null);
  const linksSupportedRef = useRef(false);
  const businessAddressSupportedRef = useRef(false);
  const hasBusinessAddress = useMemo(
    () =>
      Boolean(
        (profile.businessStreet || "").trim() ||
        (profile.businessCity || "").trim() ||
        (profile.businessState || "").trim() ||
        (profile.businessZip || "").trim()
      ),
    [profile.businessStreet, profile.businessCity, profile.businessState, profile.businessZip]
  );
  const effectiveTier = useMemo(() => {
    const tier = String(profile.subscriptionTier || "").toLowerCase();
    const status = String(profile.subscriptionStatus || "").toLowerCase();
    if (tier === "pro" || tier === "elite") return tier;
    if (status === "pro" || status === "elite") return status;
    if (status === "active" || status === "trialing") return "pro";
    return "free";
  }, [profile.subscriptionTier, profile.subscriptionStatus]);
  const canCustomizeCard = effectiveTier === "pro" || effectiveTier === "elite";
  const canUseEmailSignature = canCustomizeCard;
  const canUseVanityUrl = effectiveTier === "elite";

  useEffect(() => {
    let active = true;
    const loadSocialPngs = async () => {
      const entries = await Promise.all(
        Object.entries(SOCIAL_ICON_SVGS).map(async ([label, svg]) => {
          const png = await svgToPngDataUrl(svg, 30, 16);
          return [label, png];
        })
      );
      if (!active) return;
      setSignatureSocialPngs(Object.fromEntries(entries));
    };
    loadSocialPngs();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const url = String(profile.signatureLogoUrl || "").trim();
    if (!url) {
      setSignatureLogoRatio(null);
      return;
    }
    let active = true;
    const img = new Image();
    img.onload = () => {
      if (!active) return;
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (w > 0 && h > 0) {
        setSignatureLogoRatio(w / h);
      }
    };
    img.onerror = () => {
      if (active) setSignatureLogoRatio(null);
    };
    img.src = url;
    return () => {
      active = false;
    };
  }, [profile.signatureLogoUrl]);

  const publicBase = useMemo(() => {
    if (profile.publicLink) {
      try {
        const url = new URL(profile.publicLink);
        const parts = url.pathname.split("/").filter(Boolean);
        const basePath = parts.length > 1 ? `/${parts.slice(0, -1).join("/")}` : "/p";
        return `${url.origin}${basePath}`;
      } catch {
        // fall through
      }
    }
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/p`;
    }
    return process.env.REACT_APP_PUBLIC_BASE_URL || "https://www.clearbadge.me/p";
  }, [profile.publicLink]);

  const initials = useMemo(() => {
    const first = profile.firstName?.[0] || "";
    const last = profile.lastName?.[0] || "";
    return (first + last).trim() || "U";
  }, [profile.firstName, profile.lastName]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      if (signatureTimerRef.current) clearTimeout(signatureTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;
      userIdRef.current = userId;

      let data = null;
      const columnSets = [
        "first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,email_signature_logo,public_link,public_slug,show_email,show_phone,show_company,exclude_from_search,card_color,email_signature_color,subscription_status,subscription_tier,title,links,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,email_signature_logo,public_link,public_slug,show_email,show_phone,show_company,exclude_from_search,card_color,email_signature_color,subscription_status,subscription_tier,title,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,email_signature_logo,public_link,public_slug,show_email,show_phone,show_company,exclude_from_search,card_color,email_signature_color,subscription_status,subscription_tier,title,links,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,email_signature_logo,public_link,public_slug,show_email,show_phone,show_company,exclude_from_search,email_signature_color,subscription_status,subscription_tier,title,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,exclude_from_search,card_color,subscription_status,title,links,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,exclude_from_search,card_color,subscription_status,title,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,exclude_from_search,card_color,subscription_status,title,links,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,exclude_from_search,subscription_status,title,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,card_color,subscription_status,title,links,twitter,facebook,instagram,linkedin",
        "first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,subscription_status,title,twitter,facebook,instagram,linkedin",
      ];
      try {
        for (const cols of columnSets) {
          const res = await supabase.from("profiles").select(cols).eq("id", userId).maybeSingle();
          if (!res.error && res.data) {
            data = res.data;
            break;
          }
        }
      } catch (err) {
        // No-op: errors surface via missing data.
      }

      if (data) {
        linksSupportedRef.current = Object.prototype.hasOwnProperty.call(data, "links");
        businessAddressSupportedRef.current =
          Object.prototype.hasOwnProperty.call(data, "business_street") ||
          Object.prototype.hasOwnProperty.call(data, "business_city") ||
          Object.prototype.hasOwnProperty.call(data, "business_state") ||
          Object.prototype.hasOwnProperty.call(data, "business_zip") ||
          Object.prototype.hasOwnProperty.call(data, "business_county");
        let signedPhoto = null;
        let signedCardBackground = null;
        let signedSignatureLogo = null;
        if (data.profile_photo) {
          if (data.profile_photo.startsWith("http")) {
            signedPhoto = data.profile_photo;
          } else {
            const { data: signed, error: signedErr } = await supabase.storage
              .from("profile_photos")
              .createSignedUrl(data.profile_photo, 3600);
            if (!signedErr) {
              signedPhoto = signed?.signedUrl || null;
            }
          }
        }
        if (data.card_background_photo) {
          if (data.card_background_photo.startsWith("http")) {
            signedCardBackground = data.card_background_photo;
          } else {
            const { data: signed, error: signedErr } = await supabase.storage
              .from("profile_photos")
              .createSignedUrl(data.card_background_photo, 3600);
            if (!signedErr) {
              signedCardBackground = signed?.signedUrl || null;
            }
          }
        }
        if (data.email_signature_logo) {
          if (data.email_signature_logo.startsWith("http")) {
            signedSignatureLogo = data.email_signature_logo;
          } else {
            const { data: signed, error: signedErr } = await supabase.storage
              .from("profile_photos")
              .createSignedUrl(data.email_signature_logo, 3600);
            if (!signedErr) {
              signedSignatureLogo = signed?.signedUrl || null;
            }
          }
        }
        const baseVisibility = {
          email: data.show_email ?? true,
          phone: data.show_phone ?? true,
          company: data.show_company ?? true,
          title: true,
          businessAddress: true,
          twitter: true,
          facebook: true,
          instagram: true,
          linkedin: true,
        };

        let storedVisibility = {};
        if (userIdRef.current) {
          const saved = localStorage.getItem(`profile_visibility_flags_${userIdRef.current}`);
          if (saved) {
            try {
              storedVisibility = JSON.parse(saved) || {};
            } catch {
              storedVisibility = {};
            }
          }
        }

        const mergedVisibility = { ...baseVisibility, ...storedVisibility };

        const tier = String(data.subscription_tier || "").toLowerCase();
        const status = String(data.subscription_status || "").toLowerCase();
        const isPaid =
          tier === "pro" ||
          tier === "elite" ||
          status === "pro" ||
          status === "elite" ||
          status === "active" ||
          status === "trialing";
        const resolvedCardColor = isPaid ? data.card_color || DEFAULT_CARD_COLOR : DEFAULT_CARD_COLOR;
        const resolvedSignatureColor =
          isPaid ? data.email_signature_color || resolvedCardColor : DEFAULT_CARD_COLOR;

        const loadedProfile = {
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          email: data.email || "",
          phone: data.phone || "",
          company: data.company_agency || "",
          title: data.title || "",
          links: Array.isArray(data.links) ? data.links : [],
          businessStreet: data.business_street || "",
          businessCity: data.business_city || "",
          businessState: data.business_state || "",
          businessZip: data.business_zip || "",
          businessCounty: data.business_county || "",
          twitter: data.twitter || "",
          facebook: data.facebook || "",
          instagram: data.instagram || "",
          linkedin: data.linkedin || "",
          profilePhoto: signedPhoto,
          profilePhotoPath: data.profile_photo || "",
          signatureLogoPath: data.email_signature_logo || "",
          signatureLogoUrl: signedSignatureLogo || "",
          cardBackgroundPath: data.card_background_photo || "",
          cardBackgroundUrl: isPaid ? signedCardBackground : "",
          publicLink: data.public_link && data.public_link !== "-" ? data.public_link : "",
          publicSlug: data.public_slug || extractSlugFromLink(data.public_link || ""),
          visibility: mergedVisibility,
          subscriptionStatus: data.subscription_status || "Inactive",
          subscriptionTier: data.subscription_tier || "free",
          cardColor: resolvedCardColor,
          signatureColor: resolvedSignatureColor,
        };
        setProfile(loadedProfile);
        setOriginalProfile(loadedProfile);
        setCardColor(loadedProfile.cardColor);
        setCardColorInput(loadedProfile.cardColor);
        setCardColorDirty(false);
        setSignatureColor(loadedProfile.signatureColor);
        setSignatureColorInput(loadedProfile.signatureColor);
        setSignatureColorDirty(false);
        setVanitySlugInput(loadedProfile.publicSlug || "");
        setVanitySlugDirty(false);

        const defaultSignatureVisibility = {
          photo: Boolean(loadedProfile.profilePhoto),
          logo: Boolean(loadedProfile.signatureLogoUrl),
          name: true,
          title: mergedVisibility.title !== false,
          company: mergedVisibility.company !== false,
          email: mergedVisibility.email !== false,
          phone: mergedVisibility.phone !== false,
          publicLink: true,
          address:
            mergedVisibility.businessAddress !== false &&
            Boolean(
              (loadedProfile.businessStreet || "").trim() ||
              (loadedProfile.businessCity || "").trim() ||
              (loadedProfile.businessState || "").trim() ||
              (loadedProfile.businessZip || "").trim()
            ),
          links: Array.isArray(loadedProfile.links) && loadedProfile.links.length > 0,
          socials:
            Boolean(loadedProfile.twitter || loadedProfile.facebook || loadedProfile.instagram || loadedProfile.linkedin) &&
            (mergedVisibility.twitter !== false ||
              mergedVisibility.facebook !== false ||
              mergedVisibility.instagram !== false ||
              mergedVisibility.linkedin !== false),
        };
        let storedSignatureVisibility = {};
        if (userIdRef.current) {
          const saved = localStorage.getItem(`signature_visibility_${userIdRef.current}`);
          if (saved) {
            try {
              storedSignatureVisibility = JSON.parse(saved) || {};
            } catch {
              storedSignatureVisibility = {};
            }
          }
        }
        const nextSignatureVisibility = { ...defaultSignatureVisibility, ...storedSignatureVisibility };
        setSignatureVisibility(nextSignatureVisibility);
      }
    };

    const loadLicenseStats = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;
      userIdRef.current = userId;

      const { data: licensesData } = await supabase
        .from("licenses")
        .select("id,status,expires_on,industry,state,category,supporting_document,license_number")
        .eq("user_id", userId)
        .eq("removed", false);

      if (licensesData) {
        const docs = await Promise.all(
          licensesData.map(async (lic) => {
            let docUrl = null;
            if (lic.supporting_document) {
              if (lic.supporting_document.startsWith("http")) {
                docUrl = lic.supporting_document;
              } else {
                const { data: signedDoc, error: signedErr } = await supabase.storage
                  .from("license_docs")
                  .createSignedUrl(lic.supporting_document, 3600);
                if (!signedErr) docUrl = signedDoc?.signedUrl || null;
              }
            }
            return { ...lic, docUrl };
          })
        );

        setLicenses(
          sortLicensesByState(docs.map((lic) => ({
            id: lic.id,
            industry: lic.industry,
            state: lic.state,
            category: lic.category,
            status: lic.status,
            docUrl: lic.docUrl,
            licenseNumber: lic.license_number || lic.licenseNumber || "",
            supportingDocument: lic.supporting_document,
          })))
        );
        const activeIds = docs
          .filter((lic) => {
            const status = (lic.status || "").toLowerCase();
            return status === "active" || status === "verified";
          })
          .map((lic) => lic.id);
        const savedVisibility = userIdRef.current
          ? localStorage.getItem(`license_visibility_${userIdRef.current}`)
          : null;
        let parsed = [];
        try {
          parsed = savedVisibility ? JSON.parse(savedVisibility) : [];
        } catch {
          parsed = [];
        }
        const nextVisible = Array.isArray(parsed) && parsed.length > 0 ? parsed : activeIds;
        setVisibleLicenseIds(nextVisible.filter((id) => activeIds.includes(id)));
        const now = new Date();
        let active = 0, pending = 0, expiring = 0, issues = 0;
        licensesData.forEach((lic) => {
          const status = (lic.status || "").toLowerCase();
          if (status === "active" || status === "verified") active += 1;
          else if (status === "pending") pending += 1;
          else if (status === "issues" || status === "flagged") issues += 1;

          if (lic.expires_on) {
            const exp = new Date(lic.expires_on);
            const diffDays = (exp - now) / (1000 * 60 * 60 * 24);
            if (diffDays > 0 && diffDays <= 30) expiring += 1;
          }
        });
        setLicenseStats({ active, pending, expiring, issues });
      }
    };

    loadProfile();
    loadLicenseStats();
  }, []);

  useEffect(() => {
    const generateQr = async () => {
      if (!profile.publicLink) {
        setQrDataUrl("");
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(profile.publicLink);
        setQrDataUrl(dataUrl);
      } catch (err) {
        setError("Could not generate QR code.");
      }
    };
    generateQr();
  }, [profile.publicLink]);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCardBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canCustomizeCard) return;
    setError("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (!dataUrl) return;
      setCardBackgroundCropSrc(dataUrl);
      setShowCardBackgroundCropper(true);
    } catch (err) {
      setError(err.message || "Card background upload failed.");
    } finally {
      if (e.target) e.target.value = "";
    }
  };

  const handleApplyCardBackgroundCrop = async ({ file }) => {
    if (!file || !canCustomizeCard) return;
    setCardBackgroundUploading(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const webpFile = await convertImageToWebp(file, { maxWidth: 1800, maxHeight: 950, quality: 0.88 });
      const uploadFile = webpFile || file;
      const filePath = `${userId}/${Date.now()}-card-bg.webp`;

      const { error: uploadError } = await supabase.storage
        .from("profile_photos")
        .upload(filePath, uploadFile, { upsert: true, contentType: "image/webp" });
      if (uploadError) throw uploadError;

      const { data: signed, error: signedErr } = await supabase.storage
        .from("profile_photos")
        .createSignedUrl(filePath, 3600);
      if (signedErr) throw signedErr;

      const photoUrl = signed?.signedUrl || "";
      setProfile((prev) => ({
        ...prev,
        cardBackgroundUrl: photoUrl,
        cardBackgroundPath: filePath,
      }));
      setOriginalProfile((prev) =>
        prev ? { ...prev, cardBackgroundUrl: photoUrl, cardBackgroundPath: filePath } : prev
      );

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ card_background_photo: filePath })
        .eq("id", userId);
      if (updateError) throw updateError;

      trackGoal("digital_card_background_used", { source: "upload" });
      setShowCardBackgroundCropper(false);
      setCardBackgroundCropSrc("");
    } catch (err) {
      setError(err.message || "Card background upload failed.");
    } finally {
      setCardBackgroundUploading(false);
    }
  };

  const handleRemoveCardBackground = async () => {
    if (!canCustomizeCard) return;
    setCardBackgroundRemoving(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ card_background_photo: null })
        .eq("id", userId);
      if (updateError) throw updateError;

      setProfile((prev) => ({ ...prev, cardBackgroundUrl: "", cardBackgroundPath: "" }));
      setOriginalProfile((prev) =>
        prev ? { ...prev, cardBackgroundUrl: "", cardBackgroundPath: "" } : prev
      );
      trackGoal("digital_card_background_removed");
    } catch (err) {
      setError(err.message || "Unable to remove background.");
    } finally {
      setCardBackgroundRemoving(false);
    }
  };

  const handleSignatureLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !canUseEmailSignature) return;
    setError("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (!dataUrl) return;
      setLogoCropSrc(dataUrl);
      setShowLogoCropper(true);
    } catch (err) {
      setError(err.message || "Logo upload failed.");
    } finally {
      if (e.target) e.target.value = "";
    }
  };

  const handleApplySignatureLogoCrop = async ({ file }) => {
    if (!file || !canUseEmailSignature) return;
    setSignatureLogoUploading(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const filePath = `${userId}/${Date.now()}-signature-logo.png`;
      const { error: uploadError } = await supabase.storage
        .from("profile_photos")
        .upload(filePath, file, { upsert: true, contentType: "image/png" });
      if (uploadError) throw uploadError;

      const { data: signed, error: signedErr } = await supabase.storage
        .from("profile_photos")
        .createSignedUrl(filePath, 3600);
      if (signedErr) throw signedErr;

      const logoUrl = signed?.signedUrl || "";
      setProfile((prev) => ({
        ...prev,
        signatureLogoUrl: logoUrl,
        signatureLogoPath: filePath,
      }));
      setOriginalProfile((prev) =>
        prev ? { ...prev, signatureLogoUrl: logoUrl, signatureLogoPath: filePath } : prev
      );

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ email_signature_logo: filePath })
        .eq("id", userId);
      if (updateError) throw updateError;

      setSignatureVisibility((prev) => ({ ...prev, logo: true }));
      setShowLogoCropper(false);
      setLogoCropSrc("");
    } catch (err) {
      setError(err.message || "Logo upload failed.");
    } finally {
      setSignatureLogoUploading(false);
    }
  };

  const handleRemoveSignatureLogo = async () => {
    if (!canUseEmailSignature) return;
    setSignatureLogoRemoving(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ email_signature_logo: null })
        .eq("id", userId);
      if (updateError) throw updateError;

      setProfile((prev) => ({ ...prev, signatureLogoUrl: "", signatureLogoPath: "" }));
      setOriginalProfile((prev) =>
        prev ? { ...prev, signatureLogoUrl: "", signatureLogoPath: "" } : prev
      );
      setSignatureVisibility((prev) => ({ ...prev, logo: false }));
    } catch (err) {
      setError(err.message || "Unable to remove logo.");
    } finally {
      setSignatureLogoRemoving(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrDataUrl) return;
    const link = document.createElement("a");
    link.href = qrDataUrl;
    link.download = "verify-qr.png";
    link.click();
  };

  const handleCopyLink = () => {
    if (!profile.publicLink) return;
    navigator.clipboard?.writeText(profile.publicLink);
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 1200);
  };

  const updateProfileRow = async (userId, payload) => {
    const { error } = await supabase.from("profiles").update(payload).eq("id", userId);
    if (error) throw error;
  };

  const persistVisibility = async (nextVisibility) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) throw new Error("No session");
    const fullPayload = {
      show_email: nextVisibility.email,
      show_phone: nextVisibility.phone,
      show_company: nextVisibility.company,
    };
    await updateProfileRow(userId, fullPayload);
    localStorage.setItem(`profile_visibility_flags_${userId}`, JSON.stringify(nextVisibility));
    setOriginalProfile((prev) => (prev ? { ...prev, visibility: nextVisibility } : prev));
  };

  const handleColorChange = (value) => {
    if (!canCustomizeCard) return;
    const normalized = normalizeHex(value);
    setCardColorInput(normalized);
    const valid = isValidHex(normalized);
    if (valid) {
      setCardColor(normalized);
    }
    const savedColor = profile.cardColor || DEFAULT_CARD_COLOR;
    const matchesSaved = valid && normalized.toLowerCase() === (savedColor || "").toLowerCase();
    setCardColorDirty(!matchesSaved);
  };

  const handleSignatureColorChange = (value) => {
    if (!canUseEmailSignature) return;
    const normalized = normalizeHex(value);
    setSignatureColorInput(normalized);
    const valid = isValidHex(normalized);
    if (valid) {
      setSignatureColor(normalized);
    }
    const savedColor = profile.signatureColor || DEFAULT_CARD_COLOR;
    const matchesSaved = valid && normalized.toLowerCase() === (savedColor || "").toLowerCase();
    setSignatureColorDirty(!matchesSaved);
  };

  const darken = (hex, amount = 0.12) => {
    let c = hex.replace("#", "");
    if (c.length === 3) {
      c = c.split("").map((ch) => ch + ch).join("");
    }
    const num = parseInt(c, 16);
    const r = Math.max(0, Math.min(255, Math.floor(((num >> 16) & 255) * (1 - amount))));
    const g = Math.max(0, Math.min(255, Math.floor(((num >> 8) & 255) * (1 - amount))));
    const b = Math.max(0, Math.min(255, Math.floor((num & 255) * (1 - amount))));
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  };

  const normalizeHex = (value) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  };

  const isValidHex = (value) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value || "");

  const signatureLinks = useMemo(() => {
    const raw = Array.isArray(profile.links) ? profile.links : [];
    return raw
      .map((link) => ({
        label: (link?.label || "").trim(),
        url: normalizeWebsiteUrl(link?.url || ""),
      }))
      .filter((link) => !!link.url);
  }, [profile.links]);

  const signatureSocials = useMemo(() => {
    const socials = [];
    const twitter = normalizeSocialUrl(profile.twitter, "https://twitter.com/");
    if (twitter && profile.visibility?.twitter !== false) socials.push({ label: "Twitter", url: twitter });
    const facebook = normalizeSocialUrl(profile.facebook, "https://facebook.com/");
    if (facebook && profile.visibility?.facebook !== false) socials.push({ label: "Facebook", url: facebook });
    const instagram = normalizeSocialUrl(profile.instagram, "https://instagram.com/");
    if (instagram && profile.visibility?.instagram !== false) socials.push({ label: "Instagram", url: instagram });
    const linkedin = normalizeSocialUrl(profile.linkedin, "https://www.linkedin.com/in/");
    if (linkedin && profile.visibility?.linkedin !== false) socials.push({ label: "LinkedIn", url: linkedin });
    return socials;
  }, [profile.facebook, profile.instagram, profile.linkedin, profile.twitter, profile.visibility]);

  const hasSignaturePhoto = Boolean(profile.profilePhoto);
  const hasSignatureLogo = Boolean(profile.signatureLogoUrl);
  const hasSignatureName = Boolean((profile.firstName || "").trim() || (profile.lastName || "").trim());
  const hasSignatureTitle = Boolean((profile.title || "").trim());
  const hasSignatureCompany = Boolean((profile.company || "").trim());
  const hasSignatureEmail = Boolean((profile.email || "").trim());
  const hasSignaturePhone = Boolean((profile.phone || "").trim());
  const hasSignaturePublicLink = Boolean((profile.publicLink || "").trim());
  const hasSignatureAddress = Boolean(
    (profile.businessStreet || "").trim() ||
    (profile.businessCity || "").trim() ||
    (profile.businessState || "").trim() ||
    (profile.businessZip || "").trim()
  );
  const hasSignatureLinks = signatureLinks.length > 0;
  const hasSignatureSocials = signatureSocials.length > 0;

  const signatureDisplayColor = isValidHex(signatureColor) ? signatureColor : DEFAULT_CARD_COLOR;

  const displayColor = isValidHex(cardColor) ? cardColor : DEFAULT_CARD_COLOR;
  const pickerColor = isValidHex(cardColorInput) ? cardColorInput : DEFAULT_CARD_COLOR;
  const colorInvalid = !!cardColorInput && !isValidHex(cardColorInput);
  const signaturePickerColor = isValidHex(signatureColorInput) ? signatureColorInput : DEFAULT_CARD_COLOR;
  const signatureColorInvalid = !!signatureColorInput && !isValidHex(signatureColorInput);

  const signatureData = useMemo(() => {
    const name = signatureVisibility.name
      ? [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim()
      : "";
    const title = signatureVisibility.title ? (profile.title || "").trim() : "";
    const company = signatureVisibility.company ? (profile.company || "").trim() : "";
    const email = signatureVisibility.email ? (profile.email || "").trim() : "";
    const phone = signatureVisibility.phone ? formatPhoneDisplay(profile.phone) : "";
    const publicLink = signatureVisibility.publicLink ? (profile.publicLink || "").trim() : "";
    const publicSlug = (profile.publicSlug || extractSlugFromLink(profile.publicLink || "") || "").trim();
    const photoUrl = signatureVisibility.photo ? (profile.profilePhoto || "").trim() : "";
    const logoUrl = signatureVisibility.logo ? (profile.signatureLogoUrl || "").trim() : "";
    const address = signatureVisibility.address
      ? [profile.businessStreet, profile.businessCity, profile.businessState, profile.businessZip]
        .map((part) => (part || "").trim())
        .filter(Boolean)
        .join(", ")
      : "";
    const links = signatureVisibility.links ? signatureLinks : [];
    const socials = signatureVisibility.socials ? signatureSocials : [];
    return { name, title, company, email, phone, publicLink, publicSlug, photoUrl, logoUrl, address, links, socials };
  }, [
    profile.businessCity,
    profile.businessState,
    profile.businessStreet,
    profile.businessZip,
    profile.company,
    profile.email,
    profile.firstName,
    profile.lastName,
    profile.phone,
    profile.profilePhoto,
    profile.publicLink,
    profile.publicSlug,
    profile.signatureLogoUrl,
    profile.title,
    signatureLinks,
    signatureSocials,
    signatureVisibility,
  ]);

  const signatureHtml = useMemo(() => {
    const {
      name,
      title,
      company,
      email,
      phone,
      publicLink,
      publicSlug,
      photoUrl,
      logoUrl,
      address,
      links,
      socials,
    } = signatureData;
    const contactParts = [email, phone].filter(Boolean);
    const contactLine = contactParts.join(" | ");
    const addressLine = address ? escapeHtml(address) : "";
    const linkLine = links.length
      ? links
        .map((link) => {
          const label = link.label || formatWebsiteLabel(link.url);
          return `<a href="${escapeHtml(link.url)}" style="color: ${escapeHtml(signatureDisplayColor)}; text-decoration: none;">${escapeHtml(label)}</a>`;
        })
        .join(" | ")
      : "";
    const socialIconsHtml = socials.length
      ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top:10px;border-collapse:separate;border-spacing:6px 0;">
          <tr>
            ${socials
        .map((social) => {
          const iconPng = signatureSocialPngs[social.label] || "";
          const fallback = escapeHtml(social.label?.[0] || "");
          const iconHtml = iconPng
            ? `<img src="${escapeHtml(
              iconPng
            )}" alt="" width="30" height="30" style="display:block;width:30px;height:30px;border:0;outline:none;text-decoration:none;" />`
            : `<span style="font-size:12px;font-weight:700;display:block;line-height:18px;">${fallback}</span>`;
          return `<td width="30" height="30" align="center" valign="middle" style="width:30px;height:30px;border-radius:10px;border:1px solid #d7e6ff;background:${escapeHtml(
            signatureDisplayColor
          )}33;box-shadow:0 6px 12px rgba(0,0,0,0.05);text-align:center;line-height:0;font-size:0;">
                          <a href="${escapeHtml(
                            social.url
                          )}" style="display:block;width:30px;height:30px;text-decoration:none;line-height:0;">
                            ${iconHtml}
                          </a>
                        </td>`;
        })
        .join("")}
          </tr>
        </table>`
      : "";
    const badgeEnvBase = (process.env.REACT_APP_BADGE_URL || API_BASE || "").replace(/\/$/, "");
    const badgeWindowBase =
      typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "";
    const badgeBase = (badgeEnvBase || badgeWindowBase).replace(/^http:\/\//i, (match) =>
      badgeEnvBase && !badgeEnvBase.includes("localhost") ? "https://" : match
    );
    const badgeImgUrl =
      publicSlug && badgeBase ? `${badgeBase}/embed/badge/${encodeURIComponent(publicSlug)}.png` : "";
    const publicOrigin =
      (publicBase || "").replace(/\/p\/?$/, "") ||
      (typeof window !== "undefined" ? window.location.origin : "");
    const lookupUrl = publicLink && publicSlug
      ? `${publicOrigin}/?mode=slug&slug=${encodeURIComponent(publicSlug)}&open=1`
      : "";
    const photoHtml = photoUrl
      ? `<img src="${escapeHtml(photoUrl)}" alt="Profile photo" width="110" height="110" style="display:block;width:110px;height:110px;border-radius:50%;object-fit:cover;margin:0 auto;" />`
      : "";
    const isStandardLogo =
      Number.isFinite(signatureLogoRatio) && signatureLogoRatio > 0 && signatureLogoRatio < 2.4;
    const logoMaxWidth = isStandardLogo ? 180 : 140;
    const logoMaxHeight = isStandardLogo ? 56 : 32;
    const logoHtml = logoUrl
      ? `<div style="margin-bottom:6px;"><img src="${escapeHtml(logoUrl)}" alt="Company logo" style="display:block;max-width:${logoMaxWidth}px;max-height:${logoMaxHeight}px;height:auto;width:auto;object-fit:contain;" /></div>`
      : "";
    const badgeTarget = lookupUrl || publicLink;
    const verifiedBadge = badgeTarget && badgeImgUrl
      ? `<a href="${escapeHtml(badgeTarget)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-left:8px;vertical-align:middle;">
          ${badgeImgUrl
        ? `<img src="${escapeHtml(badgeImgUrl)}" alt="ClearBadge verification status" style="display:block;height:32px;max-height:32px;width:auto;border-radius:999px;box-shadow:0 5px 12px rgba(17,66,146,0.12);" />`
        : ""
      }
        </a>`
      : "";
    const signatureAccent = escapeHtml(signatureDisplayColor);
    return `
<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; color: #0f172a;">
  <tr>
    <td style="vertical-align: top; padding-right: 14px; text-align: center;">
      ${photoHtml}
      ${socialIconsHtml}
    </td>
    <td style="vertical-align: top; border-left: 4px solid ${signatureAccent}; padding-left: 14px;">
      ${logoHtml}
      ${name ? `<div style="font-size: 18px; font-weight: 700; margin-bottom: 4px;">${escapeHtml(name)}${verifiedBadge}</div>` : ""}
      ${title ? `<div style="font-size: 13px; color: #475569; margin-bottom: 6px;">${escapeHtml(title)}</div>` : ""}
      ${company ? `<div style="font-size: 13px; font-weight: 700; margin-bottom: 6px;">${escapeHtml(company)}</div>` : ""}
      ${contactLine ? `<div style="font-size: 13px; color: #1f2937; margin-bottom: 6px;">${escapeHtml(contactLine)}</div>` : ""}
      ${addressLine ? `<div style="font-size: 12px; color: #475569; margin-bottom: 6px;">${addressLine}</div>` : ""}
      ${linkLine ? `<div style="font-size: 12px; color: #1f2937; margin-bottom: 6px;">${linkLine}</div>` : ""}
    </td>
  </tr>
</table>`.trim();
  }, [publicBase, signatureData, signatureDisplayColor, signatureLogoRatio, signatureSocialPngs]);


  const signatureText = useMemo(() => {
    const { name, title, company, email, phone, publicLink, publicSlug, address, links, socials } = signatureData;
    const publicOrigin =
      (publicBase || "").replace(/\/p\/?$/, "") ||
      (typeof window !== "undefined" ? window.location.origin : "");
    const lookupUrl = publicLink && publicSlug
      ? `${publicOrigin}/?mode=slug&slug=${encodeURIComponent(publicSlug)}&open=1`
      : "";
    const metaParts = [title, company].filter(Boolean);
    const contactParts = [email, phone].filter(Boolean);
    const linkParts = links.map((link) => `${link.label || formatWebsiteLabel(link.url)}: ${link.url}`);
    const socialParts = socials.map((social) => `${social.label}: ${social.url}`);
    const lines = [];
    if (name) lines.push(name);
    if (metaParts.length) lines.push(metaParts.join(" · "));
    if (contactParts.length) lines.push(contactParts.join(" | "));
    if (address) lines.push(address);
    if (linkParts.length) lines.push(linkParts.join(" | "));
    if (socialParts.length) lines.push(socialParts.join(" | "));
    if (publicLink) lines.push(`ClearBadge Verified Profile: ${lookupUrl || publicLink}`);
    return lines.join("\n");
  }, [publicBase, signatureData]);

  const toggleSignatureVisibility = (key) => {
    setSignatureVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (userIdRef.current) {
        localStorage.setItem(`signature_visibility_${userIdRef.current}`, JSON.stringify(next));
      }
      return next;
    });
  };

  const handleCopySignatureHtml = async () => {
    if (!signatureHtml) return;
    try {
      if (navigator.clipboard?.write && window.ClipboardItem) {
        const htmlBlob = new Blob([signatureHtml], { type: "text/html" });
        const textBlob = new Blob([signatureText], { type: "text/plain" });
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": htmlBlob,
            "text/plain": textBlob,
          }),
        ]);
      } else {
        await navigator.clipboard?.writeText(signatureHtml);
      }
      setSignatureCopied(true);
      if (signatureTimerRef.current) clearTimeout(signatureTimerRef.current);
      signatureTimerRef.current = setTimeout(() => setSignatureCopied(false), 1400);
      trackGoal("email_signature_copied", { format: "html" });
    } catch (err) {
      setError("Unable to copy signature.");
    }
  };

  const handleCopySignatureText = async () => {
    if (!signatureText) return;
    try {
      await navigator.clipboard?.writeText(signatureText);
      setSignatureTextCopied(true);
      if (signatureTimerRef.current) clearTimeout(signatureTimerRef.current);
      signatureTimerRef.current = setTimeout(() => setSignatureTextCopied(false), 1400);
      trackGoal("email_signature_copied", { format: "text" });
    } catch (err) {
      setError("Unable to copy signature.");
    }
  };

  const handleApplyCropped = async ({ file, previewUrl }) => {
    setSavingPhoto(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const webpFile = await convertImageToWebp(file, { maxWidth: 600, maxHeight: 600, quality: 0.86 });
      const filePath = `${userId}/${Date.now()}-avatar.webp`;
      const uploadFile = webpFile || file;

      const { error: uploadError } = await supabase.storage
        .from("profile_photos")
        .upload(filePath, uploadFile, { upsert: true, contentType: "image/webp" });
      if (uploadError) throw uploadError;

      const { data: signed, error: urlError } = await supabase.storage
        .from("profile_photos")
        .createSignedUrl(filePath, 3600);
      if (urlError) throw urlError;

      const photoUrl = signed?.signedUrl;
      setProfile({ ...profile, profilePhoto: photoUrl, profilePhotoPath: filePath });

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ profile_photo: filePath })
        .eq("id", userId);
      if (updateError) throw updateError;

      setShowCropper(false);
      setCropSrc("");
    } catch (err) {
      setError(err.message || "Photo upload failed.");
    } finally {
      setSavingPhoto(false);
    }
  };

  const toggleVisibility = async (key) => {
    const nextVisibility = { ...profile.visibility, [key]: !profile.visibility[key] };
    setProfile({
      ...profile,
      visibility: nextVisibility,
    });
    try {
      await persistVisibility(nextVisibility);
    } catch (err) {
      setError(err.message || "Unable to save visibility.");
      setProfile((prev) => ({
        ...prev,
        visibility: { ...prev.visibility, [key]: !nextVisibility[key] },
      }));
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");
      if (!profile.firstName || !profile.lastName || !profile.email) {
        throw new Error("First name, last name, and email are required.");
      }

      const payload = {
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        company_agency: profile.company,
        title: profile.title?.trim() || null,
        twitter: profile.twitter?.trim() || null,
        facebook: profile.facebook?.trim() || null,
        instagram: profile.instagram?.trim() || null,
        linkedin: profile.linkedin?.trim() || null,
        show_email: profile.visibility.email,
        show_phone: profile.visibility.phone,
        show_company: profile.visibility.company,
      };
      if (canCustomizeCard) {
        payload.card_color = profile.cardColor || DEFAULT_CARD_COLOR;
      }

      await updateProfileRow(userId, payload);

      // Save links if the column exists; don't block profile save if it doesn't.
      const cleanedLinks = Array.isArray(profile.links)
        ? profile.links
          .map((l) => ({
            label: (l?.label || "").trim(),
            url: (l?.url || "").trim(),
          }))
          .filter((l) => !!l.url)
        : [];
      if (linksSupportedRef.current || cleanedLinks.length > 0) {
        const { error: linksError } = await supabase.from("profiles").update({ links: cleanedLinks }).eq("id", userId);
        // Don't block save if DB isn't updated yet
        if (linksError) {
          // swallow
        }
      }

      const businessPayload = {
        business_street: profile.businessStreet?.trim() || null,
        business_city: profile.businessCity?.trim() || null,
        business_state: profile.businessState?.trim() || null,
        business_zip: profile.businessZip?.trim() || null,
        business_county: profile.businessCounty?.trim() || null,
      };
      if (businessAddressSupportedRef.current || hasBusinessAddress) {
        const { error: businessError } = await supabase.from("profiles").update(businessPayload).eq("id", userId);
        if (businessError) {
          // swallow
        }
      }

      setOriginalProfile({ ...profile });
      setIsEditing(false);
    } catch (err) {
      setError(err.message || "Unable to save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveCardColor = async () => {
    if (!canCustomizeCard) return;
    const normalized = normalizeHex(cardColorInput);
    if (!isValidHex(normalized)) {
      setError("Enter a valid hex color (e.g. #4f9fe0).");
      return;
    }
    setCardColorSaving(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ card_color: normalized })
        .eq("id", userId);
      if (updateError) throw updateError;

      setProfile((prev) => ({ ...prev, cardColor: normalized }));
      setOriginalProfile((prev) => (prev ? { ...prev, cardColor: normalized } : prev));
      setCardColor(normalized);
      setCardColorInput(normalized);
      setCardColorDirty(false);
      trackGoal("digital_card_color_changed", { color: normalized.toLowerCase() });
    } catch (err) {
      setError(err.message || "Unable to save card color.");
    } finally {
      setCardColorSaving(false);
    }
  };

  const handleSaveSignatureColor = async () => {
    if (!canUseEmailSignature) return;
    const normalized = normalizeHex(signatureColorInput);
    if (!isValidHex(normalized)) {
      setError("Enter a valid hex color (e.g. #4f9fe0).");
      return;
    }
    setSignatureColorSaving(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ email_signature_color: normalized })
        .eq("id", userId);
      if (updateError) throw updateError;

      setProfile((prev) => ({ ...prev, signatureColor: normalized }));
      setOriginalProfile((prev) => (prev ? { ...prev, signatureColor: normalized } : prev));
      setSignatureColor(normalized);
      setSignatureColorInput(normalized);
      setSignatureColorDirty(false);
    } catch (err) {
      setError(err.message || "Unable to save signature color.");
    } finally {
      setSignatureColorSaving(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalError("");
    setPortalBusy(true);
    try {
      await startStripePortal({ returnPath: "/my-info" });
    } catch (err) {
      setPortalError(err.message || "Unable to open billing portal.");
      setPortalBusy(false);
    }
  };

  const handleVanitySlugChange = (value) => {
    const normalized = slugify(value);
    setVanitySlugInput(normalized);
    setVanitySlugDirty(normalized !== (profile.publicSlug || ""));
  };

  const handleSaveVanitySlug = async () => {
    if (!canUseVanityUrl) return;
    const normalized = slugify(vanitySlugInput);
    if (!normalized) {
      setError("Enter a vanity URL.");
      return;
    }
    if (normalized.length < 3) {
      setError("Vanity URL must be at least 3 characters.");
      return;
    }
    if (normalized === profile.publicSlug) {
      setVanitySlugDirty(false);
      return;
    }
    setVanitySlugSaving(true);
    setError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) throw new Error("No session");

      const { data: existing, error: checkError } = await supabase
        .from("profiles")
        .select("id")
        .eq("public_slug", normalized)
        .maybeSingle();
      if (checkError) throw checkError;
      if (existing && existing.id !== userId) {
        throw new Error("That vanity URL is already taken.");
      }

      const nextLink = `${publicBase}/${normalized}`;
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ public_slug: normalized, public_link: nextLink })
        .eq("id", userId);
      if (updateError) throw updateError;

      setProfile((prev) => ({ ...prev, publicSlug: normalized, publicLink: nextLink }));
      setOriginalProfile((prev) =>
        prev ? { ...prev, publicSlug: normalized, publicLink: nextLink } : prev
      );
      setVanitySlugDirty(false);
    } catch (err) {
      setError(err.message || "Unable to update vanity URL.");
    } finally {
      setVanitySlugSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (originalProfile) {
      setProfile(originalProfile);
      const resetColor = originalProfile.cardColor || DEFAULT_CARD_COLOR;
      setCardColor(resetColor);
      setCardColorInput(resetColor);
      setCardColorDirty(false);
      const resetSignature = originalProfile.signatureColor || DEFAULT_CARD_COLOR;
      setSignatureColor(resetSignature);
      setSignatureColorInput(resetSignature);
      setSignatureColorDirty(false);
    }
    setIsEditing(false);
    setError("");
  };

  const handleLicenseVisibilityToggle = (id) => {
    setVisibleLicenseIds((prev) => {
      const next = prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id];
      if (userIdRef.current) {
        // Only persist active IDs to avoid stale pending ones
        const activeIds = licenses
          .filter((lic) => (lic.status || "").toLowerCase() === "active" || (lic.status || "").toLowerCase() === "verified")
          .map((lic) => lic.id);
        const filtered = next.filter((v) => activeIds.includes(v));
        localStorage.setItem(`license_visibility_${userIdRef.current}`, JSON.stringify(filtered));
        return filtered;
      }
      return next;
    });
  };

  const activeLicenses = sortLicensesByState(licenses.filter((lic) => {
    const status = (lic.status || "").toLowerCase();
    return status === "active" || status === "verified";
  }));
  const visibleLicensesForCard = sortLicensesByState(activeLicenses.filter((lic) => visibleLicenseIds.includes(lic.id)));

  return (
    <div className="page">
      {showCropper && cropSrc && (
        <CropperModal
          src={cropSrc}
          onApply={handleApplyCropped}
          onClose={() => {
            setShowCropper(false);
            setCropSrc("");
          }}
        />
      )}
      {showLogoCropper && logoCropSrc && (
        <LogoCropperModal
          src={logoCropSrc}
          onApply={handleApplySignatureLogoCrop}
          onClose={() => {
            setShowLogoCropper(false);
            setLogoCropSrc("");
          }}
        />
      )}
      {showCardBackgroundCropper && cardBackgroundCropSrc && (
        <CardBackgroundCropperModal
          src={cardBackgroundCropSrc}
          onApply={handleApplyCardBackgroundCrop}
          onClose={() => {
            setShowCardBackgroundCropper(false);
            setCardBackgroundCropSrc("");
          }}
        />
      )}
      <div className="page-header">
        <div>
          <p className="eyebrow">Manage your personal information and public verification page</p>
          <h1 data-page-heading="true">My Info</h1>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="tab-card">
        <div className="tabs">
          <button className={tab === "personal" ? "active" : ""} onClick={() => setTab("personal")}>
            Personal Info
          </button>
          <button className={tab === "public" ? "active" : ""} onClick={() => setTab("public")}>
            Public Verification Page
          </button>
          <button className={tab === "signature" ? "active" : ""} onClick={() => setTab("signature")}>
            Email Signature
          </button>
        </div>

        {tab === "personal" ? (
          <div className={`tab-content ${!isEditing ? "readonly" : ""}`} style={{ position: "relative", paddingBottom: 60 }}>
            <p className="muted form-section-label">My Info</p>
            <div className="form-row dual">
              <div>
                <label htmlFor="myinfo-first-name">First name *</label>
                <input
                  id="myinfo-first-name"
                  type="text"
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                  placeholder="Enter first name"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <label htmlFor="myinfo-last-name">Last name *</label>
                <input
                  id="myinfo-last-name"
                  type="text"
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  placeholder="Enter last name"
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="myinfo-email">Email address *</label>
              <input
                id="myinfo-email"
                type="email"
                value={profile.email}
                placeholder="Email"
                readOnly
              />
              <p className="hint">Email changes are restricted. Contact support to update your login email.</p>
            </div>
            <div className="form-row">
              <label htmlFor="myinfo-phone">Phone number</label>
              <input
                id="myinfo-phone"
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="Phone"
                disabled={!isEditing}
              />
            </div>
            <div className="form-row">
              <label htmlFor="myinfo-company">Company/Agency</label>
              <input
                id="myinfo-company"
                type="text"
                value={profile.company}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                placeholder="Optional"
                disabled={!isEditing}
              />
            </div>
            <div className="form-row">
              <label htmlFor="myinfo-title">Title</label>
              <input
                id="myinfo-title"
                type="text"
                value={profile.title}
                onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                placeholder="e.g. CEO, Real Estate Agent"
                disabled={!isEditing}
              />
            </div>
            <p className="muted form-section-label">Business Info</p>
            <div className="form-row">
              <label htmlFor="myinfo-link-label-0">Links</label>
              <div className="links-list">
                {(Array.isArray(profile.links) ? profile.links : []).map((link, idx) => (
                  <div className="link-item" key={`link-${idx}`}>
                    <input
                      id={`myinfo-link-label-${idx}`}
                      type="text"
                      aria-label={`Link ${idx + 1} label`}
                      value={link?.label || ""}
                      onChange={(e) =>
                        setProfile((prev) => {
                          const next = Array.isArray(prev.links) ? [...prev.links] : [];
                          next[idx] = { ...(next[idx] || {}), label: e.target.value };
                          return { ...prev, links: next };
                        })
                      }
                      placeholder="Label (optional)"
                      disabled={!isEditing}
                    />
                    <input
                      id={`myinfo-link-url-${idx}`}
                      type="text"
                      aria-label={`Link ${idx + 1} URL`}
                      value={link?.url || ""}
                      onChange={(e) =>
                        setProfile((prev) => {
                          const next = Array.isArray(prev.links) ? [...prev.links] : [];
                          next[idx] = { ...(next[idx] || {}), url: e.target.value };
                          return { ...prev, links: next };
                        })
                      }
                      placeholder="https://yourwebsite.com"
                      disabled={!isEditing}
                    />
                    {isEditing && (
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() =>
                          setProfile((prev) => {
                            const next = Array.isArray(prev.links) ? prev.links.filter((_, i) => i !== idx) : [];
                            return { ...prev, links: next };
                          })
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                {(!Array.isArray(profile.links) || profile.links.length === 0) && (
                  <p className="muted small" style={{ margin: 0 }}>
                    No links added yet.
                  </p>
                )}
              </div>
              {isEditing && (
                <button
                  type="button"
                  className="ghost small"
                  style={{ alignSelf: "flex-start", marginTop: 8 }}
                  onClick={() =>
                    setProfile((prev) => ({
                      ...prev,
                      links: [...(Array.isArray(prev.links) ? prev.links : []), { label: "", url: "" }],
                    }))
                  }
                >
                  + Add link
                </button>
              )}
              <p className="hint">Optional. These links will appear on your digital card.</p>
            </div>
            <div className="form-row dual">
              <div>
                <label htmlFor="myinfo-business-street">Business/Office address</label>
                <input
                  id="myinfo-business-street"
                  type="text"
                  value={profile.businessStreet}
                  onChange={(e) => setProfile({ ...profile, businessStreet: e.target.value })}
                  placeholder="Street address"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <label htmlFor="myinfo-business-city">City</label>
                <input
                  id="myinfo-business-city"
                  type="text"
                  value={profile.businessCity}
                  onChange={(e) => setProfile({ ...profile, businessCity: e.target.value })}
                  placeholder="City"
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="form-row dual">
              <div>
                <label htmlFor="myinfo-business-state">State</label>
                <input
                  id="myinfo-business-state"
                  type="text"
                  value={profile.businessState}
                  onChange={(e) => setProfile({ ...profile, businessState: e.target.value })}
                  placeholder="State"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <label htmlFor="myinfo-business-zip">Zip</label>
                <input
                  id="myinfo-business-zip"
                  type="text"
                  value={profile.businessZip}
                  onChange={(e) => setProfile({ ...profile, businessZip: e.target.value })}
                  placeholder="Zip"
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="form-row">
              <label htmlFor="myinfo-business-county">County (optional)</label>
              <input
                id="myinfo-business-county"
                type="text"
                value={profile.businessCounty}
                onChange={(e) => setProfile({ ...profile, businessCounty: e.target.value })}
                placeholder="County"
                disabled={!isEditing}
              />
            </div>
            <p className="muted form-section-label">Socials</p>
            <div className="form-row dual">
              <div>
                <label htmlFor="myinfo-twitter">Twitter</label>
                <input
                  id="myinfo-twitter"
                  type="text"
                  value={profile.twitter}
                  onChange={(e) => setProfile({ ...profile, twitter: e.target.value })}
                  placeholder="@handle or full URL"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <label htmlFor="myinfo-facebook">Facebook</label>
                <input
                  id="myinfo-facebook"
                  type="text"
                  value={profile.facebook}
                  onChange={(e) => setProfile({ ...profile, facebook: e.target.value })}
                  placeholder="Profile or page URL"
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="form-row dual">
              <div>
                <label htmlFor="myinfo-instagram">Instagram</label>
                <input
                  id="myinfo-instagram"
                  type="text"
                  value={profile.instagram}
                  onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                  placeholder="@handle or full URL"
                  disabled={!isEditing}
                />
              </div>
              <div>
                <label htmlFor="myinfo-linkedin">LinkedIn</label>
                <input
                  id="myinfo-linkedin"
                  type="text"
                  value={profile.linkedin}
                  onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                  placeholder="Profile URL"
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="myinfo-profile-photo">Profile photo</label>
              <div className="photo-row">
                <div className="avatar">
                  {profile.profilePhoto ? (
                    <img src={profile.profilePhoto} alt="Profile" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                <button className="ghost" type="button" onClick={() => fileRef.current?.click()} disabled={savingPhoto || !isEditing}>
                  {savingPhoto ? "Uploading..." : "Upload photo"}
                </button>
                <input id="myinfo-profile-photo" type="file" accept="image/*" ref={fileRef} onChange={handlePhoto} hidden />
              </div>
              <p className="hint">Optional. JPG or PNG. Max 5MB.</p>
            </div>
            <div className="button-row button-row-edit">
              {!isEditing ? (
                <button className="ghost" type="button" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
              ) : (
                <>
                  <button className="ghost" type="button" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button className="primary dark" type="button" onClick={handleSaveProfile} disabled={savingProfile}>
                    {savingProfile ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>
        ) : tab === "public" ? (
          <div className="tab-content">
            <div className="form-row">
              <label htmlFor="myinfo-public-link">Your public link</label>
              <div className="link-row">
                <input
                  id="myinfo-public-link"
                  type="text"
                  value={profile.publicLink}
                  readOnly
                />
                <button
                  className={`ghost copy-btn ${copied ? "copied" : ""}`}
                  type="button"
                  onClick={handleCopyLink}
                >
                  <span className="copy-text" aria-hidden={copied}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="copy-icon">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
                    </svg>
                  </span>
                  <span className="copy-check" aria-hidden={!copied}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="copy-icon">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </span>
                  <span className="sr-only">Copy public link</span>
                </button>
              </div>
            </div>
            <div className="form-row">
              <label htmlFor="myinfo-vanity-url">Vanity URL</label>
              {canUseVanityUrl ? (
                <>
                  <div className="vanity-row">
                    <span className="vanity-prefix">{publicBase}/</span>
                    <input
                      id="myinfo-vanity-url"
                      type="text"
                      className="vanity-input"
                      value={vanitySlugInput}
                      onChange={(e) => handleVanitySlugChange(e.target.value)}
                      placeholder="your-name"
                    />
                    {vanitySlugDirty && (
                      <button
                        className="primary dark"
                        type="button"
                        onClick={handleSaveVanitySlug}
                        disabled={vanitySlugSaving}
                      >
                        {vanitySlugSaving ? "Saving..." : "Save"}
                      </button>
                    )}
                  </div>
                  <p className="hint">Use letters, numbers, and hyphens.</p>
                </>
              ) : (
                <div className="panel vanity-upsell">
                  <h4>Own your link.</h4>
                  <p className="muted">
                    Elite members claim a custom vanity URL for maximum visibility.{" "}
                    <Link
                      to="/plans"
                      onClick={() =>
                        trackGoal("upsell_interacted", {
                          upsell_id: "vanity_url_upsell",
                          action: "claim_now",
                        })
                      }
                    >
                      Claim Now
                    </Link>
                  </p>
                </div>
              )}
            </div>

            <div className="qr-row">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR code" className="qr-image" />
              ) : (
                <div className="qr-placeholder">QR Code</div>
              )}
              <button className="ghost" type="button" disabled={!qrDataUrl} onClick={handleDownloadQR}>
                Download QR
              </button>
            </div>

            <div className="form-row">
              <label htmlFor="myinfo-card-color-hex">Card background color</label>
              <div className={`color-row ${canCustomizeCard ? "" : "is-disabled"}`}>
                <div className="color-swatch" aria-label="Card color">
                  <span className="color-swatch-fill" style={{ background: displayColor }} />
                  <input
                    id="myinfo-card-color-picker"
                    type="color"
                    className="color-picker"
                    value={pickerColor}
                    onChange={(e) => handleColorChange(e.target.value)}
                    aria-label="Choose card color"
                    disabled={!canCustomizeCard}
                  />
                </div>
                <input
                  id="myinfo-card-color-hex"
                  type="text"
                  className={`color-hex-input ${colorInvalid ? "input-error" : ""}`}
                  value={cardColorInput}
                  onChange={(e) => handleColorChange(e.target.value)}
                  placeholder={DEFAULT_CARD_COLOR}
                  disabled={!canCustomizeCard}
                />
                {cardColorDirty && canCustomizeCard && (
                  <button
                    className="primary dark color-save"
                    type="button"
                    onClick={handleSaveCardColor}
                    disabled={cardColorSaving || colorInvalid}
                  >
                    {cardColorSaving ? "Saving..." : "Save color"}
                  </button>
                )}
              </div>
              {colorInvalid && <p className="error-inline">Enter a valid hex color.</p>}
              {canCustomizeCard ? (
                <p className="hint">Choose a color; the overlay texture will auto-adjust darker.</p>
              ) : null}
            </div>
            <div className="form-row">
              <label htmlFor="myinfo-card-background-image">Card background image</label>
              <div className={`card-bg-row ${canCustomizeCard ? "" : "is-disabled"}`}>
                <div className={`card-bg-preview ${profile.cardBackgroundUrl ? "has-image" : ""}`}>
                  {profile.cardBackgroundUrl ? (
                    <img src={profile.cardBackgroundUrl} alt="Card background preview" />
                  ) : (
                    <div className="card-bg-placeholder">Default texture</div>
                  )}
                </div>
                <div className="card-bg-actions">
                  <button
                    className="primary dark"
                    type="button"
                    onClick={() => cardBackgroundRef.current?.click()}
                    disabled={!canCustomizeCard || cardBackgroundUploading}
                  >
                    {cardBackgroundUploading ? "Uploading..." : "Upload background"}
                  </button>
                  <button
                    className="ghost"
                    type="button"
                    onClick={handleRemoveCardBackground}
                    disabled={!canCustomizeCard || cardBackgroundRemoving || !profile.cardBackgroundUrl}
                  >
                    {cardBackgroundRemoving ? "Removing..." : "Use default background"}
                  </button>
                </div>
                <input
                  id="myinfo-card-background-image"
                  ref={cardBackgroundRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleCardBackgroundUpload}
                  disabled={!canCustomizeCard}
                />
              </div>
              <p className="hint">Optional. Upload a photo background under 5MB. Recommended ratio: 16:9.</p>
              {!canCustomizeCard && (
                <div className="panel vanity-upsell" style={{ marginTop: 8 }}>
                  <h4>Make it yours.</h4>
                  <p className="muted">
                    Pro lets you personalized your card fully.{" "}
                    <Link
                      to="/plans"
                      onClick={() =>
                        trackGoal("upsell_interacted", {
                          upsell_id: "card_background_upsell",
                          action: "unlock_now",
                        })
                      }
                    >
                      Unlock Now
                    </Link>
                  </p>
                </div>
              )}
            </div>

            <div className="visibilities">
              <p className="muted">What’s visible to the public</p>
              <div className="visibilities-grid">
                <div className="vis-column">
                  <p className="muted vis-label">Info</p>
                  <label className="toggle-row">
                    <span>Show Email</span>
                    <input type="checkbox" checked={profile.visibility.email} onChange={() => toggleVisibility("email")} />
                  </label>
                  <label className="toggle-row">
                    <span>Show Phone</span>
                    <input type="checkbox" checked={profile.visibility.phone} onChange={() => toggleVisibility("phone")} />
                  </label>
                  <label className="toggle-row">
                    <span>Show Company</span>
                    <input type="checkbox" checked={profile.visibility.company} onChange={() => toggleVisibility("company")} />
                  </label>
                  {profile.title && (
                    <label className="toggle-row">
                      <span>Show Title</span>
                      <input type="checkbox" checked={profile.visibility.title} onChange={() => toggleVisibility("title")} />
                    </label>
                  )}
                  {hasBusinessAddress && (
                    <label className="toggle-row">
                      <span>Show Business Address</span>
                      <input type="checkbox" checked={profile.visibility.businessAddress} onChange={() => toggleVisibility("businessAddress")} />
                    </label>
                  )}
                </div>
                <div className="vis-column">
                  <p className="muted vis-label">Socials</p>
                  {profile.twitter && (
                    <label className="toggle-row">
                      <span>Show Twitter</span>
                      <input type="checkbox" checked={profile.visibility.twitter} onChange={() => toggleVisibility("twitter")} />
                    </label>
                  )}
                  {profile.facebook && (
                    <label className="toggle-row">
                      <span>Show Facebook</span>
                      <input type="checkbox" checked={profile.visibility.facebook} onChange={() => toggleVisibility("facebook")} />
                    </label>
                  )}
                  {profile.instagram && (
                    <label className="toggle-row">
                      <span>Show Instagram</span>
                      <input type="checkbox" checked={profile.visibility.instagram} onChange={() => toggleVisibility("instagram")} />
                    </label>
                  )}
                  {profile.linkedin && (
                    <label className="toggle-row">
                      <span>Show LinkedIn</span>
                      <input type="checkbox" checked={profile.visibility.linkedin} onChange={() => toggleVisibility("linkedin")} />
                    </label>
                  )}
                  {!profile.twitter && !profile.facebook && !profile.instagram && !profile.linkedin && (
                    <p className="muted small" style={{ marginTop: 4 }}>No social links added yet.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="panel license-toggle-panel">
              <div className="panel-header">
                <h4 className="muted vis-label">Licenses</h4>
              </div>
              <div className="license-toggle-list">
                {activeLicenses.length === 0 && <p className="muted">No active licenses to show.</p>}
                {activeLicenses.map((lic) => (
                  <label key={lic.id} className="toggle-row license-toggle-row">
                    <div>
                      <strong>{lic.industry || "License"}</strong>
                      <div className="muted">
                        {lic.licenseNumber ? `#${lic.licenseNumber}` : ""}
                        {lic.state ? ` · ${lic.state}` : ""}
                        {!lic.licenseNumber && !lic.state && "—"}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={visibleLicenseIds.includes(lic.id)}
                      onChange={() => handleLicenseVisibilityToggle(lic.id)}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div className="public-preview">
              <DigitalCard
                profile={profile}
                initials={initials}
                licenses={visibleLicensesForCard}
                stats={licenseStats}
                cardColor={cardColor}
                cardColorDark={darken(cardColor, 0.12)}
                cardBackgroundUrl={profile.cardBackgroundUrl}
                publicSlug={profile.publicSlug}
                profileId={userIdRef.current || ""}
              />
            </div>
          </div>
        ) : (
          <div className="tab-content">
            {canUseEmailSignature ? (
              <div className="form-grid">
                <div className="panel" style={{ marginBottom: 16 }}>
                  <div className="panel-header">
                    <h4>Signature elements</h4>
                  </div>
                  <p className="muted" style={{ marginTop: 6 }}>
                    Choose which details appear in your signature.
                  </p>
                  <div className="visibilities-grid" style={{ marginTop: 12 }}>
                    <div className="vis-column">
                      <p className="muted vis-label">Basics</p>
                      {hasSignaturePhoto && (
                        <label className="toggle-row">
                          <span>Show profile photo</span>
                          <input type="checkbox" checked={signatureVisibility.photo} onChange={() => toggleSignatureVisibility("photo")} />
                        </label>
                      )}
                      {hasSignatureLogo && (
                        <label className="toggle-row">
                          <span>Show company logo</span>
                          <input type="checkbox" checked={signatureVisibility.logo} onChange={() => toggleSignatureVisibility("logo")} />
                        </label>
                      )}
                      {hasSignatureName && (
                        <label className="toggle-row">
                          <span>Show name</span>
                          <input type="checkbox" checked={signatureVisibility.name} onChange={() => toggleSignatureVisibility("name")} />
                        </label>
                      )}
                      {hasSignatureTitle && (
                        <label className="toggle-row">
                          <span>Show title</span>
                          <input type="checkbox" checked={signatureVisibility.title} onChange={() => toggleSignatureVisibility("title")} />
                        </label>
                      )}
                      {hasSignatureCompany && (
                        <label className="toggle-row">
                          <span>Show company</span>
                          <input type="checkbox" checked={signatureVisibility.company} onChange={() => toggleSignatureVisibility("company")} />
                        </label>
                      )}
                      {hasSignaturePublicLink && (
                        <label className="toggle-row">
                          <span>Show verified profile link</span>
                          <input type="checkbox" checked={signatureVisibility.publicLink} onChange={() => toggleSignatureVisibility("publicLink")} />
                        </label>
                      )}
                    </div>
                    <div className="vis-column">
                      <p className="muted vis-label">Contact</p>
                      {hasSignatureEmail && (
                        <label className="toggle-row">
                          <span>Show email</span>
                          <input type="checkbox" checked={signatureVisibility.email} onChange={() => toggleSignatureVisibility("email")} />
                        </label>
                      )}
                      {hasSignaturePhone && (
                        <label className="toggle-row">
                          <span>Show phone</span>
                          <input type="checkbox" checked={signatureVisibility.phone} onChange={() => toggleSignatureVisibility("phone")} />
                        </label>
                      )}
                      {hasSignatureAddress && (
                        <label className="toggle-row">
                          <span>Show business address</span>
                          <input type="checkbox" checked={signatureVisibility.address} onChange={() => toggleSignatureVisibility("address")} />
                        </label>
                      )}
                    </div>
                    <div className="vis-column">
                      <p className="muted vis-label">Links</p>
                      {hasSignatureLinks && (
                        <label className="toggle-row">
                          <span>Show website links</span>
                          <input type="checkbox" checked={signatureVisibility.links} onChange={() => toggleSignatureVisibility("links")} />
                        </label>
                      )}
                      {hasSignatureSocials && (
                        <label className="toggle-row">
                          <span>Show social links</span>
                          <input type="checkbox" checked={signatureVisibility.socials} onChange={() => toggleSignatureVisibility("socials")} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <label htmlFor="myinfo-signature-logo">Company logo</label>
                  <div className="signature-logo-row">
                    <div className="signature-logo-preview">
                      {profile.signatureLogoUrl ? (
                        <img src={profile.signatureLogoUrl} alt="Company logo preview" />
                      ) : (
                        <div className="card-bg-placeholder">No logo uploaded</div>
                      )}
                    </div>
                    <div className="signature-logo-actions">
                      <button
                        className="primary dark"
                        type="button"
                        onClick={() => signatureLogoRef.current?.click()}
                        disabled={signatureLogoUploading}
                      >
                        {signatureLogoUploading ? "Uploading..." : "Upload logo"}
                      </button>
                      <button
                        className="ghost"
                        type="button"
                        onClick={handleRemoveSignatureLogo}
                        disabled={signatureLogoRemoving || !profile.signatureLogoUrl}
                      >
                        {signatureLogoRemoving ? "Removing..." : "Remove logo"}
                      </button>
                    </div>
                    <input
                      id="myinfo-signature-logo"
                      ref={signatureLogoRef}
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleSignatureLogoUpload}
                    />
                  </div>
                  <p className="hint">
                    Optional. Upload a JPG or PNG under 5MB (transparent PNG works best). Recommended ratios: 5:1 (wide)
                    or 4:3 (standard).
                  </p>
                </div>
                <div className="form-row">
                  <label htmlFor="myinfo-signature-color-hex">Signature accent color</label>
                  <div className="color-row">
                    <div className="color-swatch" aria-label="Signature color">
                      <span className="color-swatch-fill" style={{ background: signatureDisplayColor }} />
                      <input
                        id="myinfo-signature-color-picker"
                        type="color"
                        className="color-picker"
                        value={signaturePickerColor}
                        onChange={(e) => handleSignatureColorChange(e.target.value)}
                        aria-label="Choose signature color"
                      />
                    </div>
                    <input
                      id="myinfo-signature-color-hex"
                      type="text"
                      className={`color-hex-input ${signatureColorInvalid ? "input-error" : ""}`}
                      value={signatureColorInput}
                      onChange={(e) => handleSignatureColorChange(e.target.value)}
                      placeholder={DEFAULT_CARD_COLOR}
                    />
                    {signatureColorDirty && (
                      <button
                        className="primary dark color-save"
                        type="button"
                        onClick={handleSaveSignatureColor}
                        disabled={signatureColorSaving || signatureColorInvalid}
                      >
                        {signatureColorSaving ? "Saving..." : "Save color"}
                      </button>
                    )}
                  </div>
                  {signatureColorInvalid && <p className="error-inline">Enter a valid hex color.</p>}
                  <p className="hint">Controls the divider and social icon accents.</p>
                </div>
                <div className="form-row">
                  <label htmlFor="myinfo-signature-preview">Email signature preview</label>
                  <div
                    id="myinfo-signature-preview"
                    className="signature-preview"
                    dangerouslySetInnerHTML={{ __html: signatureHtml }}
                  />
                  <p className="hint">Copy and paste into Gmail, Outlook, or Apple Mail.</p>
                </div>
                <div className="form-row">
                  <label htmlFor="myinfo-signature-html">Signature HTML</label>
                  <textarea
                    id="myinfo-signature-html"
                    className="signature-code"
                    value={signatureHtml}
                    readOnly
                    rows={6}
                  />
                </div>
                <div className="signature-actions">
                  <button className="primary dark" type="button" onClick={handleCopySignatureHtml}>
                    {signatureCopied ? "Copied!" : "Copy signature"}
                  </button>
                  <button className="ghost" type="button" onClick={handleCopySignatureText}>
                    {signatureTextCopied ? "Copied!" : "Copy plain text"}
                  </button>
                </div>
                {/* <p className="hint signature-note">
                  Uses your public verification page settings (name, title, company, phone, email).
                </p> */}
              </div>
            ) : (
              <div className="panel">
                <h4>Unlock Email Signature</h4>
                <p className="muted" style={{ marginTop: 6 }}>
                  Create a custom ClearBadge™ verified email signature to your emails so recipients can instantly confirm
                  your credentials before they reply.
                </p>
                <div className="button-row" style={{ marginTop: 12 }}>
                  <Link
                    className="primary dark"
                    to="/plans"
                    style={{ textDecoration: "none" }}
                    onClick={() =>
                      trackGoal("upsell_interacted", {
                        upsell_id: "email_signature_upsell",
                        action: "unlock_email_signature",
                      })
                    }
                  >
                    Unlock Email Signature
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {tab === "personal" && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-header">
            <h3>My Subscription</h3>
          </div>
          <p className="muted">Manage your subscription for digital business card and verification access.</p>
          {portalError && <p className="error-message">{portalError}</p>}
          <div className="button-row" style={{ marginTop: 10 }}>
            <button className="primary dark" type="button" onClick={handleManageSubscription} disabled={portalBusy}>
              {portalBusy ? "Opening..." : "Manage Subscription"}
            </button>
            <button
              className="ghost"
              type="button"
              onClick={() => (window.location.href = "mailto:support@clearbadge.org?subject=Cancel%20Subscription")}
            >
              Cancel / Contact Support
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
